// Stores files added from this device in IndexedDB so they survive reloads. They never
// leave the device: settings sync strips local items before upload.

const DB_NAME = 'kcl-canvas-media';
const STORE = 'files';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (!('indexedDB' in window)) return Promise.reject(new Error('IndexedDB unavailable'));
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error ?? new Error('IndexedDB open failed'));
    };
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        t.oncomplete = () => resolve(req.result);
        t.onerror = () => reject(t.error ?? new Error('IndexedDB transaction failed'));
        t.onabort = () => reject(t.error ?? new Error('IndexedDB transaction aborted'));
      }),
  );
}

export async function saveLocalFile(id: string, file: Blob): Promise<void> {
  await tx('readwrite', (s) => s.put(file, id));
  // Ask the browser not to evict our files under storage pressure (best effort).
  void navigator.storage?.persist?.().catch(() => undefined);
}

export function loadLocalFile(id: string): Promise<Blob | undefined> {
  return tx<Blob | undefined>('readonly', (s) => s.get(id) as IDBRequest<Blob | undefined>);
}

export async function deleteLocalFile(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id));
}

export async function pruneLocalFiles(keep: Set<string>): Promise<void> {
  const keys = await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
  await Promise.all(keys.filter((k) => !keep.has(String(k))).map((k) => deleteLocalFile(String(k))));
}
